import {
  ClientPrimaryNavigationKey,
  ClientSecondaryNavigationKey,
  GlobalPortalNavigationKey,
} from "../config/navigation";

export const CLINICIAN_PORTAL_NAVIGATION_EVENT =
  "efa:clinician-portal-navigation";

export type ClinicianPortalRoute =
  | { kind: "home"; globalKey: "portal" }
  | { kind: "team"; globalKey: "team" }
  | { kind: "team-clients"; globalKey: "team" }
  | {
      kind: "team-client-detail";
      globalKey: "team";
      patientId: string;
    }
  | { kind: "team-homework"; globalKey: "team" }
  | {
      kind: "team-homework-detail";
      globalKey: "team";
      patientId: string;
      homeworkAssignmentId: string;
    }
  | { kind: "team-reports"; globalKey: "team" }
  | { kind: "clinics"; globalKey: "clinics" }
  | { kind: "clinic-new"; globalKey: "clinics" }
  | {
      kind: "clinic-edit";
      globalKey: "clinics";
      clinicId: string;
    }
  | { kind: "therapists"; globalKey: "therapists" }
  | { kind: "admin-clients"; globalKey: "admin-clients" }
  | { kind: "therapist-new"; globalKey: "therapists" }
  | {
      kind: "therapist-edit";
      globalKey: "therapists";
      therapistId: string;
    }
  | { kind: "activity-registry"; globalKey: "activity-registry" }
  | { kind: "lov-catalogs"; globalKey: "lists-of-values" }
  | {
      kind: "lov-options";
      globalKey: "lists-of-values";
      catalogKey: string;
    }
  | { kind: "clients"; globalKey: "clients" }
  | { kind: "client-new"; globalKey: "clients" }
  | {
      kind: "client-edit";
      globalKey: "clients";
      patientId: string;
    }
  | { kind: "activity-templates"; globalKey: "templates" }
  | { kind: "activity-template-overview"; globalKey: "templates" }
  | { kind: "activity-template-new"; globalKey: "templates" }
  | {
      kind: "activity-template-detail";
      globalKey: "templates";
      templateId: string;
    }
  | {
      kind: "activity-template-edit";
      globalKey: "templates";
      templateId: string;
    }
  | {
      kind: "activity-template-steps";
      globalKey: "templates";
      templateId: string;
    }
  | { kind: "homework-activities"; globalKey: "homework" }
  | { kind: "homework-activity-new"; globalKey: "homework" }
  | { kind: "coping-skills"; globalKey: "coping-skills" }
  | {
      kind: "caseload-engagement";
      globalKey: "caseload-engagement";
    }
  | {
      kind: "homework-overview";
      globalKey: "homework-overview";
    }
  | {
      kind: "completion-by-device";
      globalKey: "completion-by-device";
    }
  | {
      kind: "value-engagement";
      globalKey: "value-engagement";
    }
  | {
      kind: "homework-activity-detail";
      globalKey: "homework";
      patientId: string;
      homeworkAssignmentId: string;
    }
  | {
      kind: "homework-activity-steps";
      globalKey: "homework";
      patientId: string;
      homeworkAssignmentId: string;
    }
  | {
      kind: "client-profile";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: "profile";
    }
  | {
      kind: "client-current-homework";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: "current-homework";
    }
  | {
      kind: "client-session-transcript";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      sessionId: string;
      primaryKey: "sessions";
    }
  | {
      kind: "client-sessions";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: "sessions";
    }
  | {
      kind: "client-homework-history";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: "homework-history";
    }
  | {
      kind: "client-homework-detail";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      homeworkAssignmentId: string;
      primaryKey: "current-homework" | "homework-history";
    }
  | {
      kind: "client-clinical-trends";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: "insights";
      secondaryKey: "clinical-trends";
    }
  | {
      kind: "client-usage-engagement";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: "insights";
      secondaryKey: "usage-engagement";
    }
  | {
      kind: "client-practice-calendar";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: "insights";
      secondaryKey: "practice-calendar";
    }
  | {
      kind: "client-progress-reports";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: "insights";
      secondaryKey: "progress-reports";
    }
  | {
      kind: "client-treatment";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: "treatment";
      secondaryKey:
        | "values"
        | "personalized-coping-skills"
        | "plans-documents"
        | "expectancies";
    }
  | {
      kind: "client-deferred";
      globalKey: "clients" | "team";
      clientSource?: "team";
      patientId: string;
      primaryKey: Exclude<ClientPrimaryNavigationKey, "profile">;
      secondaryKey?: ClientSecondaryNavigationKey;
    }
  | {
      kind: "global-deferred";
      globalKey: Exclude<GlobalPortalNavigationKey, "clients">;
    }
  | { kind: "not-found"; globalKey?: GlobalPortalNavigationKey };

function normalizePath(pathname: string): string {
  if (!pathname) return "/";
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
}

function decodePatientId(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value);
    return decoded ? decoded : null;
  } catch {
    return null;
  }
}

export function isClinicianPortalPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === "/clinician-portal" || path.startsWith("/clinician-portal/");
}

export function matchClinicianPortalRoute(
  pathname: string,
): ClinicianPortalRoute {
  const path = normalizePath(pathname);

  if (path === "/clinician-portal") {
    return { kind: "home", globalKey: "portal" };
  }
  if (path === "/clinician-portal/team") {
    return { kind: "team", globalKey: "team" };
  }
  if (path === "/clinician-portal/team/clients") {
    return { kind: "team-clients", globalKey: "team" };
  }
  const teamClientWorkspaceMatch = path.match(
    /^\/clinician-portal\/team\/clients\/([^/]+)(?:\/(.*))?$/,
  );
  if (teamClientWorkspaceMatch) {
    const patientId = decodePatientId(teamClientWorkspaceMatch[1]);
    if (!patientId) return { kind: "not-found", globalKey: "team" };
    const suffix = teamClientWorkspaceMatch[2] || "profile";
    if (suffix === "edit" || suffix.endsWith("/steps")) {
      return { kind: "not-found", globalKey: "team" };
    }
    const assignedRoute = matchClinicianPortalRoute(
      `/clinician-portal/clients/${encodeURIComponent(patientId)}/${suffix}`,
    );
    if (
      !assignedRoute.kind.startsWith("client-") ||
      assignedRoute.kind === "client-edit"
    ) {
      return { kind: "not-found", globalKey: "team" };
    }
    return {
      ...assignedRoute,
      globalKey: "team",
      clientSource: "team",
    } as ClinicianPortalRoute;
  }
  if (path === "/clinician-portal/team/homework") {
    return { kind: "team-homework", globalKey: "team" };
  }
  const teamHomeworkDetailMatch = path.match(
    /^\/clinician-portal\/team\/homework\/([^/]+)\/([^/]+)$/,
  );
  if (teamHomeworkDetailMatch) {
    const patientId = decodePatientId(teamHomeworkDetailMatch[1]);
    const homeworkAssignmentId = decodePatientId(teamHomeworkDetailMatch[2]);
    return patientId && homeworkAssignmentId
      ? {
          kind: "team-homework-detail",
          globalKey: "team",
          patientId,
          homeworkAssignmentId,
        }
      : { kind: "not-found", globalKey: "team" };
  }
  if (path === "/clinician-portal/team/reports") {
    return { kind: "team-reports", globalKey: "team" };
  }
  if (path === "/clinician-portal/admin/activity-registry") {
    return { kind: "activity-registry", globalKey: "activity-registry" };
  }
  if (path === "/clinician-portal/admin/lists-of-values") {
    return { kind: "lov-catalogs", globalKey: "lists-of-values" };
  }
  const lovOptionsMatch = path.match(
    /^\/clinician-portal\/admin\/lists-of-values\/([^/]+)$/,
  );
  if (lovOptionsMatch) {
    const catalogKey = decodePatientId(lovOptionsMatch[1]);
    return catalogKey
      ? { kind: "lov-options", globalKey: "lists-of-values", catalogKey }
      : { kind: "not-found", globalKey: "lists-of-values" };
  }
  if (path === "/clinician-portal/admin/clinics/new") {
    return { kind: "clinic-new", globalKey: "clinics" };
  }
  const clinicEditMatch = path.match(
    /^\/clinician-portal\/admin\/clinics\/([^/]+)\/edit$/,
  );
  if (clinicEditMatch) {
    const clinicId = decodePatientId(clinicEditMatch[1]);
    return clinicId
      ? { kind: "clinic-edit", globalKey: "clinics", clinicId }
      : { kind: "not-found", globalKey: "clinics" };
  }
  if (path === "/clinician-portal/admin/clinics") {
    return { kind: "clinics", globalKey: "clinics" };
  }
  if (path === "/clinician-portal/admin/therapists/new") {
    return { kind: "therapist-new", globalKey: "therapists" };
  }
  const therapistEditMatch = path.match(
    /^\/clinician-portal\/admin\/therapists\/([^/]+)\/edit$/,
  );
  if (therapistEditMatch) {
    const therapistId = decodePatientId(therapistEditMatch[1]);
    return therapistId
      ? { kind: "therapist-edit", globalKey: "therapists", therapistId }
      : { kind: "not-found", globalKey: "therapists" };
  }
  if (path === "/clinician-portal/admin/therapists") {
    return { kind: "therapists", globalKey: "therapists" };
  }
  if (path === "/clinician-portal/admin/clients") {
    return { kind: "admin-clients", globalKey: "admin-clients" };
  }
  if (path === "/clinician-portal/clients") {
    return { kind: "clients", globalKey: "clients" };
  }
  if (path === "/clinician-portal/clients/new") {
    return { kind: "client-new", globalKey: "clients" };
  }
  if (path === "/clinician-portal/activity-templates") {
    return { kind: "activity-templates", globalKey: "templates" };
  }
  if (path === "/clinician-portal/activity-templates/overview") {
    return { kind: "activity-template-overview", globalKey: "templates" };
  }
  if (path === "/clinician-portal/activity-templates/new") {
    return { kind: "activity-template-new", globalKey: "templates" };
  }
  if (path === "/clinician-portal/homework-activities") {
    return { kind: "homework-activities", globalKey: "homework" };
  }
  if (path === "/clinician-portal/homework-activities/new") {
    return { kind: "homework-activity-new", globalKey: "homework" };
  }
  if (path === "/clinician-portal/coping-skills") {
    return { kind: "coping-skills", globalKey: "coping-skills" };
  }
  if (path === "/clinician-portal/caseload-engagement") {
    return {
      kind: "caseload-engagement",
      globalKey: "caseload-engagement",
    };
  }
  if (path === "/clinician-portal/homework-overview") {
    return {
      kind: "homework-overview",
      globalKey: "homework-overview",
    };
  }
  if (path === "/clinician-portal/completion-by-device") {
    return {
      kind: "completion-by-device",
      globalKey: "completion-by-device",
    };
  }
  if (path === "/clinician-portal/value-engagement") {
    return {
      kind: "value-engagement",
      globalKey: "value-engagement",
    };
  }

  const templateEditMatch = path.match(
    /^\/clinician-portal\/activity-templates\/([^/]+)\/edit$/,
  );
  if (templateEditMatch) {
    const templateId = decodePatientId(templateEditMatch[1]);
    return templateId
      ? { kind: "activity-template-edit", globalKey: "templates", templateId }
      : { kind: "not-found", globalKey: "templates" };
  }

  const templateStepsMatch = path.match(
    /^\/clinician-portal\/activity-templates\/([^/]+)\/steps$/,
  );
  if (templateStepsMatch) {
    const templateId = decodePatientId(templateStepsMatch[1]);
    return templateId
      ? { kind: "activity-template-steps", globalKey: "templates", templateId }
      : { kind: "not-found", globalKey: "templates" };
  }

  const templateDetailMatch = path.match(
    /^\/clinician-portal\/activity-templates\/([^/]+)$/,
  );
  if (templateDetailMatch) {
    const templateId = decodePatientId(templateDetailMatch[1]);
    return templateId
      ? { kind: "activity-template-detail", globalKey: "templates", templateId }
      : { kind: "not-found", globalKey: "templates" };
  }

  const homeworkStepsMatch = path.match(
    /^\/clinician-portal\/homework-activities\/([^/]+)\/([^/]+)\/steps$/,
  );
  if (homeworkStepsMatch) {
    const patientId = decodePatientId(homeworkStepsMatch[1]);
    const homeworkAssignmentId = decodePatientId(homeworkStepsMatch[2]);
    return patientId && homeworkAssignmentId
      ? {
          kind: "homework-activity-steps",
          globalKey: "homework",
          patientId,
          homeworkAssignmentId,
        }
      : { kind: "not-found", globalKey: "homework" };
  }

  const globalHomeworkDetailMatch = path.match(
    /^\/clinician-portal\/homework-activities\/([^/]+)\/([^/]+)$/,
  );
  if (globalHomeworkDetailMatch) {
    const patientId = decodePatientId(globalHomeworkDetailMatch[1]);
    const homeworkAssignmentId = decodePatientId(globalHomeworkDetailMatch[2]);
    return patientId && homeworkAssignmentId
      ? {
          kind: "homework-activity-detail",
          globalKey: "homework",
          patientId,
          homeworkAssignmentId,
        }
      : { kind: "not-found", globalKey: "homework" };
  }

  const clientMatch = path.match(
    /^\/clinician-portal\/clients\/([^/]+)(?:\/(.*))?$/,
  );
  if (!clientMatch) return { kind: "not-found" };

  const patientId = decodePatientId(clientMatch[1]);
  if (!patientId) return { kind: "not-found", globalKey: "clients" };

  const suffix = clientMatch[2] ?? "profile";
  if (suffix === "edit") {
    return { kind: "client-edit", globalKey: "clients", patientId };
  }
  if (suffix === "profile") {
    return {
      kind: "client-profile",
      globalKey: "clients",
      patientId,
      primaryKey: "profile",
    };
  }
  if (suffix === "current-homework") {
    return {
      kind: "client-current-homework",
      globalKey: "clients",
      patientId,
      primaryKey: "current-homework",
    };
  }

  const homeworkDetailMatch = suffix.match(
    /^(current-homework|homework-history)\/([^/]+)$/,
  );
  if (homeworkDetailMatch) {
    const homeworkAssignmentId = decodePatientId(homeworkDetailMatch[2]);
    if (!homeworkAssignmentId) {
      return { kind: "not-found", globalKey: "clients" };
    }
    return {
      kind: "client-homework-detail",
      globalKey: "clients",
      patientId,
      homeworkAssignmentId,
      primaryKey: homeworkDetailMatch[1] as
        | "current-homework"
        | "homework-history",
    };
  }

  const transcriptMatch = suffix.match(/^sessions\/([^/]+)$/);
  if (transcriptMatch) {
    const sessionId = decodePatientId(transcriptMatch[1]);
    return sessionId && sessionId.trim() && sessionId.length <= 200
      ? {
          kind: "client-session-transcript",
          globalKey: "clients",
          patientId,
          sessionId,
          primaryKey: "sessions",
        }
      : { kind: "not-found", globalKey: "clients" };
  }
  if (suffix === "sessions") {
    return { kind: "client-sessions", globalKey: "clients", patientId, primaryKey: "sessions" };
  }

  if (suffix === "homework-history") {
    return {
      kind: "client-homework-history",
      globalKey: "clients",
      patientId,
      primaryKey: "homework-history",
    };
  }

  const treatmentMatch = suffix.match(
    /^treatment(?:\/(values|personalized-coping-skills|plans-documents|expectancies))?$/,
  );
  if (treatmentMatch) {
    if (
      !treatmentMatch[1] ||
      treatmentMatch[1] === "values" ||
      treatmentMatch[1] === "personalized-coping-skills" ||
      treatmentMatch[1] === "plans-documents" ||
      treatmentMatch[1] === "expectancies"
    ) {
      return {
        kind: "client-treatment",
        globalKey: "clients",
        patientId,
        primaryKey: "treatment",
        secondaryKey:
          treatmentMatch[1] === "personalized-coping-skills"
            ? "personalized-coping-skills"
            : treatmentMatch[1] === "values"
              ? "values"
              : treatmentMatch[1] === "expectancies"
                ? "expectancies"
                : "plans-documents",
      };
    }
    return {
      kind: "client-deferred",
      globalKey: "clients",
      patientId,
      primaryKey: "treatment",
      secondaryKey: treatmentMatch[1] as ClientSecondaryNavigationKey,
    };
  }

  const insightsMatch = suffix.match(
    /^insights(?:\/(usage-engagement|practice-calendar|clinical-trends|progress-reports))?$/,
  );
  if (insightsMatch) {
    if (!insightsMatch[1] || insightsMatch[1] === "usage-engagement") {
      return {
        kind: "client-usage-engagement",
        globalKey: "clients",
        patientId,
        primaryKey: "insights",
        secondaryKey: "usage-engagement",
      };
    }
    if (insightsMatch[1] === "clinical-trends") {
      return {
        kind: "client-clinical-trends",
        globalKey: "clients",
        patientId,
        primaryKey: "insights",
        secondaryKey: "clinical-trends",
      };
    }
    if (insightsMatch[1] === "practice-calendar") {
      return {
        kind: "client-practice-calendar",
        globalKey: "clients",
        patientId,
        primaryKey: "insights",
        secondaryKey: "practice-calendar",
      };
    }
    if (insightsMatch[1] === "progress-reports") {
      return {
        kind: "client-progress-reports",
        globalKey: "clients",
        patientId,
        primaryKey: "insights",
        secondaryKey: "progress-reports",
      };
    }
    return {
      kind: "client-deferred",
      globalKey: "clients",
      patientId,
      primaryKey: "insights",
      secondaryKey:
        (insightsMatch[1] as ClientSecondaryNavigationKey | undefined) ??
        "usage-engagement",
    };
  }

  return { kind: "not-found", globalKey: "clients" };
}

export function withPortalContext(path: string): string {
  const hashIndex = path.indexOf("#");
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
  const pathWithoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const queryIndex = pathWithoutHash.indexOf("?");
  const pathname =
    queryIndex >= 0 ? pathWithoutHash.slice(0, queryIndex) : pathWithoutHash;
  const search = new URLSearchParams(
    queryIndex >= 0 ? pathWithoutHash.slice(queryIndex + 1) : "",
  );
  search.delete("clinicianCode");
  const currentDb =
    typeof window === "undefined"
      ? ""
      : (new URLSearchParams(window.location.search).get("db")?.trim() ?? "");
  if (currentDb && !search.has("db")) search.set("db", currentDb);
  const query = search.toString();
  return `${pathname}${query ? `?${query}` : ""}${hash}`;
}

/**
 * @deprecated Clinician authentication is session-scoped. This compatibility
 * wrapper now returns the same credential-free URL as withPortalContext.
 */
export function withClinicianCode(
  path: string,
  _clinicianCode: string,
): string {
  return withPortalContext(path);
}

export function navigateWithinPortal(href: string): void {
  window.history.pushState({}, "", href);
  window.dispatchEvent(new Event(CLINICIAN_PORTAL_NAVIGATION_EVENT));
}

export function replaceWithinPortal(href: string): void {
  window.history.replaceState({}, "", href);
  window.dispatchEvent(new Event(CLINICIAN_PORTAL_NAVIGATION_EVENT));
}
